import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import DiagnosticUiPanel from "@/components/sat/DiagnosticUiPanel";
import { buildClientSatSyncQueueBanner } from "@/lib/clientSatSyncQueueBanner";
import { useSatUiDiagnose } from "@/lib/useSatUiDiagnose";

/**
 * Interruptor de sincronización automática SAT por cliente.
 * Si el cliente ya existe, persiste de inmediato con PATCH /api/clients/:id/sat-auto-sync.
 */
export function ClientSatAutoSyncToggle({
  form,
  setForm,
  clientId = null,
  disabled = false,
  onPersisted,
}) {
  const [saving, setSaving] = useState(false);
  const reconcileAttemptedRef = useRef(null);
  const checked = Boolean(form?.modulo_descarga_activo);

  useEffect(() => {
    if (!clientId || !checked) return undefined;
    const st = (form?.estado_sincronizacion || "").trim().toLowerCase();
    const hasHistoricalMarker = Boolean(String(form?.sat_sync_completed_at || "").trim());
    if (st === "procesando") return undefined;
    if (hasHistoricalMarker) return undefined;
    const attemptKey = `${clientId}:${st || "empty"}`;
    if (reconcileAttemptedRef.current === attemptKey) return undefined;
    reconcileAttemptedRef.current = attemptKey;

    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post(`/clients/${clientId}/sat-sync-reconcile`);
        if (cancelled || !data) return;
        setForm((prev) => ({ ...prev, ...data }));
        if (onPersisted) onPersisted(data);
        const nextSt = (data.estado_sincronizacion || "").trim().toLowerCase();
        if (nextSt === "pendiente" && !data.sat_sync_has_local_cfdis) {
          reconcileAttemptedRef.current = null;
        }
      } catch {
        reconcileAttemptedRef.current = null;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    clientId,
    checked,
    form?.estado_sincronizacion,
    form?.sat_sync_completed_at,
    onPersisted,
    setForm,
  ]);

  const queueStatus = useMemo(() => {
    if (!checked || !clientId) return null;
    return buildClientSatSyncQueueBanner(form);
  }, [
    checked,
    clientId,
    form?.estado_sincronizacion,
    form?.sat_sync_completed_at,
    form?.sat_sync_historical_complete,
    form?.has_local_cfdis,
    form?.sat_downloads_paused,
    form?.sat_downloads_paused_reason,
    form?.sat_5002_next_retry_at,
    form?.sat_cooling_off_until,
  ]);

  const handleCheckedChange = async (nextChecked) => {
    const value = nextChecked === true;
    if (value && !(form?.fecha_inicio_descarga || "").trim()) {
      toast.error("Indique la fecha inicio de descarga histórica antes de activar la sincronización");
      return;
    }
    const nextForm = { ...form, modulo_descarga_activo: value };
    setForm(nextForm);

    if (!clientId) {
      return;
    }

    setSaving(true);
    try {
      const { data } = await api.patch(`/clients/${clientId}/sat-auto-sync`, {
        modulo_descarga_activo: value,
      });
      setForm((prev) => ({ ...prev, ...data }));
      if (onPersisted) onPersisted(data);
      const activation = data?.sat_sync_activation;
      if (value && activation?.ui_message) {
        toast.success(activation.ui_message);
      } else if (value) {
        toast.success(
          "Sincronización automática activada. El sistema descargará en segundo plano.",
        );
      } else {
        toast.success("Sincronización automática desactivada");
      }
    } catch (err) {
      setForm({ ...form, modulo_descarga_activo: !value });
      toast.error(formatApiError(err, "No se pudo guardar la sincronización automática"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="col-span-2 rounded-md border border-slate-200 bg-slate-50/80 p-4 space-y-3"
      data-testid="client-sat-auto-sync-field"
    >
      <div className="flex items-start gap-3">
        <Checkbox
          id="modulo-descarga-activo"
          checked={checked}
          disabled={disabled || saving}
          onCheckedChange={handleCheckedChange}
          data-testid="client-modulo-descarga-activo"
          className="mt-0.5"
        />
        <div className="space-y-1 flex-1">
          <Label
            htmlFor="modulo-descarga-activo"
            className="text-sm font-semibold text-[#0F2D5C] cursor-pointer leading-snug"
          >
            Habilitar Sincronización Automática SAT
          </Label>
          <p className="text-xs text-slate-600 leading-relaxed m-0">
            Desde la fecha de inicio configurada, el sistema descargará los CFDI del SAT en segundo plano hasta
            ponerse al corriente y luego actualizará los días nuevos automáticamente. Requiere e.firma (FIEL) en
            la pestaña Certificados y FIEL.
            {clientId ? (
              <span className="block mt-1 text-[#0F2D5C]">
                Al activar, la descarga histórica se inicia de inmediato (puede tardar horas o días según el
                historial).
              </span>
            ) : (
              <span className="block mt-1 text-slate-500">
                Guarde el cliente primero; luego podrá activar la sincronización.
              </span>
            )}
          </p>
        </div>
        {saving ? (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-slate-500" aria-label="Guardando" />
        ) : null}
      </div>

      {queueStatus ? (
        <div
          className={`rounded-sm border px-3 py-2 text-xs space-y-1 ${queueStatus.className}`}
          data-testid="client-sat-sync-queue-status"
        >
          <p className="m-0 font-semibold">{queueStatus.label}</p>
          {queueStatus.detail ? <p className="m-0 opacity-90">{queueStatus.detail}</p> : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Piso histórico para sync automática y gap analyzer (requerido si sync activa).
 */
export function ClientFechaInicioDescargaField({ form, setForm, disabled = false }) {
  const { enabled: diagnoseEnabled } = useSatUiDiagnose();
  const syncOn = Boolean(form?.modulo_descarga_activo);
  const value = (form?.fecha_inicio_descarga || "").slice(0, 10);

  return (
    <div
      className="col-span-2 rounded-md border border-slate-200 bg-white p-3 space-y-2"
      data-testid="client-fecha-inicio-descarga-field"
    >
      <Label htmlFor="fecha-inicio-descarga" className="text-xs font-semibold text-[#0F2D5C]">
        Fecha inicio de descarga histórica
        {syncOn ? <span className="text-red-600 font-normal"> *</span> : null}
      </Label>
      <input
        id="fecha-inicio-descarga"
        type="date"
        value={value}
        disabled={disabled}
        required={syncOn}
        onChange={(e) =>
          setForm({ ...form, fecha_inicio_descarga: (e.target.value || "").slice(0, 10) })
        }
        className="flex h-9 w-full max-w-xs rounded-sm border border-slate-200 bg-white px-3 py-1 text-sm"
        data-testid="client-fecha-inicio-descarga"
      />
      <p className="text-[11px] text-slate-600 leading-relaxed m-0">
        El sistema descargará CFDI desde esta fecha hasta ponerse al corriente. No se escanearán meses
        anteriores. Si el cliente ya operaba sin este campo, use la fecha de hoy o la primera sync deseada.
      </p>
      {syncOn && diagnoseEnabled ? (
        <DiagnosticUiPanel enabled={diagnoseEnabled}>
          <p
            className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded-sm px-2.5 py-2 leading-relaxed m-0"
            data-testid="client-fecha-inicio-carril2-hint"
          >
            <strong>Primera carga histórica (Carril 2):</strong> si el rango desde esta fecha es amplio
            (varios meses), el SAT procesa la solicitud masiva de XML en su cola oficial y puede tardar{" "}
            <strong>24 a 48 horas</strong> antes de que los paquetes estén listos. La sincronización diaria
            posterior usa metadatos ligeros y descarga quirúrgica de XML faltantes.
          </p>
        </DiagnosticUiPanel>
      ) : null}
    </div>
  );
}

/**
 * Primer mes de seguimiento fiscal: periodos anteriores se marcan «No aplica» en la matriz anual.
 */
export function ClientFechaInicioControlField({ form, setForm, disabled = false }) {
  const raw = (form?.fecha_inicio_control || "").trim();
  const value = raw.length >= 7 ? raw.slice(0, 7) : "";

  return (
    <div
      className="col-span-2 rounded-md border border-slate-200 bg-white p-3 space-y-2"
      data-testid="client-fecha-inicio-control-field"
    >
      <Label htmlFor="fecha-inicio-control" className="text-xs font-semibold text-[#0F2D5C]">
        Inicio de control / seguimiento
      </Label>
      <input
        id="fecha-inicio-control"
        type="month"
        value={value}
        disabled={disabled}
        onChange={(e) => {
          const ym = (e.target.value || "").slice(0, 7);
          setForm({
            ...form,
            fecha_inicio_control: ym ? `${ym}-01` : "",
          });
        }}
        className="flex h-9 w-full max-w-xs rounded-sm border border-slate-200 bg-white px-3 py-1 text-sm"
        data-testid="client-fecha-inicio-control"
      />
      <p className="text-[11px] text-slate-600 leading-relaxed m-0">
        Meses anteriores a esta fecha aparecen como <strong>No aplica</strong> en el seguimiento anual
        (sin ❌ de vencido). Úselo si el cliente arranca a mitad de año o solo quiere operar el año
        actual. Vacío = se evalúan todos los periodos.
      </p>
    </div>
  );
}

export default ClientSatAutoSyncToggle;
